// Catalog.ID Client — `chat_sign` sub-key certificates (v1)
// ============================================================================
// Normative format: apps/chat/docs/impl/chat-sign-certs.md
// Parent plan:      apps/chat/docs/plans/migration.md §3 (`chat_sign`), §14 M1.
//
// `chat_sign` is TWO-LEVEL. An account-level ROOT lives in the CID keybag and
// signs nothing but CERTS for per-machine SUB-KEYS; the sub-keys sign
// messages. This module is the whole of the cert layer: issue, verify, revoke.
//
// WHAT THIS MODULE DOES NOT DO — read this before trusting an `ok: true`:
// it verifies ONE link of the chain (message-signing sub-key → cert issued by
// this root). Whether the ROOT itself was valid at time `t` — its
// `registry_signing_key` claim-track window under the `chat_root` class — is
// the consumer's job at M2+ and is deliberately out of scope. That is why
// `verifyCert` takes the root public key as a parameter instead of resolving
// it: resolution and root-validity live above this layer.
//
// Pure functions, no I/O, no module state. `async` only because the
// fingerprint helper is `crypto.subtle.digest`.
//
// TWO TRAPS THIS MODULE EXISTS TO CLOSE (migration §17):
//   1. Wire form. `signHybrid` returns a JSON WRAPPER of the two signature
//      halves in separate base64 fields. Durable records — this one included —
//      carry the CONCATENATED 3373-byte form (`wrapperToCombinedBytes`),
//      base64-encoded once. The wrapper is never serialized here.
//   2. Sub-keys are hybrid, ALWAYS. A cert whose `subkeyPubkey` is not exactly
//      1984 bytes is refused as a FORMAT error, before signature verification
//      — an Ed25519-only sub-key "optimization" (~3 KB/message) violates the
//      durable-content rule and there is no negotiation path.
// ============================================================================

import { canonicalize } from '@catalog-canonical-json';
import * as aes from './aes';
import {
    signHybrid, verifyHybrid, wrapperToCombinedBytes, combinedToWrapper, signingPublicKeyFromSeed,
    computeHybridSigningKeyFingerprint,
    HYBRID_PUBLIC_KEY_LEN, HYBRID_SECRET_SEED_LEN, HYBRID_COMBINED_SIGNATURE_LEN,
} from './signing';

// ── constants ───────────────────────────────────────────────────────────

export const CHAT_SIGN_CERT_TYPE = 'chat_sign_cert.v1';
export const CHAT_SIGN_REVOCATION_TYPE = 'chat_sign_cert_revocation.v1';

/** Decided 2026-08-26 (migration §3): default cert lifetime. */
export const CHAT_CERT_DEFAULT_VALIDITY_DAYS = 60;
/** Decided 2026-08-26: hard cap. Enforced at BOTH issuance and verification —
 *  a verifier that only checked the window would honour a ten-year cert minted
 *  by a buggy or coerced issuance path. */
export const CHAT_CERT_MAX_VALIDITY_DAYS = 90;

const MS_PER_DAY = 86_400_000;
const MAX_ACCOUNT_CID_LEN = 64;

/** ISO-8601 UTC, second precision. No offsets but `Z`, no fractional seconds,
 *  no space separator — a second accepted format would be a canonicalization
 *  hazard (spec §3.3). */
const TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
const FINGERPRINT_RE = /^[0-9a-f]{128}$/;

const CERT_BODY_FIELDS = [
    'accountCid', 'issuedAt', 'rootFingerprint', 'subkeyPubkey', 'type',
    'validFrom', 'validTo',
] as const;
const REVOCATION_BODY_FIELDS = [
    'revokedAt', 'rootFingerprint', 'subkeyFingerprint', 'type',
] as const;

// ── types ───────────────────────────────────────────────────────────────

export interface ChatSignCertBody {
    type: typeof CHAT_SIGN_CERT_TYPE;
    accountCid: string;
    /** 128 lowercase hex — SHA-512 of the root's 1984-byte combined pubkey. */
    rootFingerprint: string;
    /** base64; MUST decode to exactly 1984 bytes. */
    subkeyPubkey: string;
    validFrom: string;
    validTo: string;
    /** Informational: when the ceremony ran. Does NOT gate validity. */
    issuedAt: string;
}

/** The transported/stored record. The envelope itself is unsigned — a verifier
 *  re-canonicalizes `body`, so transit re-ordering or pretty-printing is
 *  harmless. */
export interface ChatSignCert {
    body: ChatSignCertBody;
    /** base64 of the COMBINED 3373-byte hybrid signature. Never a wrapper. */
    signature: string;
}

export interface ChatSignRevocationBody {
    type: typeof CHAT_SIGN_REVOCATION_TYPE;
    rootFingerprint: string;
    subkeyFingerprint: string;
    revokedAt: string;
}

export interface ChatSignCertRevocation {
    body: ChatSignRevocationBody;
    signature: string;
}

export type CertVerifyFailureCode =
    | 'malformed'
    | 'bad-type'
    | 'bad-signature-encoding'
    | 'subkey-not-hybrid'
    | 'bad-timestamp'
    | 'bad-window'
    | 'lifetime-too-long'
    | 'bad-root-key'
    | 'root-mismatch'
    | 'bad-signature'
    | 'not-yet-valid'
    | 'expired'
    | 'revoked';

export type CertVerifyResult =
    | {
        ok: true;
        body: ChatSignCertBody;
        /** The certified sub-key, decoded (1984 bytes). */
        subkeyPubkey: Uint8Array;
        /** 128 lowercase hex — SHA-512 of `subkeyPubkey`. */
        subkeyFingerprint: string;
        /** Revocations addressed to THIS root whose signature did not verify.
         *  Non-zero is an attack signal worth logging (spec §4.3). */
        ignoredRevocations: number;
    }
    | {
        ok: false;
        code: CertVerifyFailureCode;
        message: string;
        ignoredRevocations: number;
    };

export type RevocationVerifyFailureCode =
    | 'malformed' | 'bad-type' | 'bad-signature-encoding' | 'bad-timestamp'
    | 'bad-root-key' | 'root-mismatch' | 'bad-signature';

export type RevocationVerifyResult =
    | { ok: true; body: ChatSignRevocationBody }
    | { ok: false; code: RevocationVerifyFailureCode; message: string };

export interface IssueCertOptions {
    /** The issuing account's CID. */
    accountCid: string;
    /** 1…90; defaults to {@link CHAT_CERT_DEFAULT_VALIDITY_DAYS}. */
    validityDays?: number;
    /** Window start. Defaults to now. Truncated to second precision. */
    validFrom?: Date;
    /** Recorded as `issuedAt`. Defaults to now. */
    issuedAt?: Date;
}

export interface VerifyCertOptions {
    /** Evaluation instant. For archived history this is the objective time the
     *  message was signed (checkpoint bracket where anchored), NOT `new
     *  Date()` — see migration §3. */
    at: Date;
    /** Candidate revocations. Each is signature-verified here; unverifiable
     *  ones are IGNORED, never honoured (spec §4.3). */
    revocations?: readonly ChatSignCertRevocation[];
}

// ── small helpers ───────────────────────────────────────────────────────

/** ISO-8601 UTC at second precision (spec §3.3). */
export function toCertTimestamp(d: Date): string {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) {
        throw new Error('bad-timestamp');
    }
    return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function parseCertTimestamp(s: string): number | null {
    if (typeof s !== 'string' || !TIMESTAMP_RE.test(s)) return null;
    const ms = Date.parse(s);
    if (Number.isNaN(ms)) return null;
    // Round-trip guard: `2026-02-30T00:00:00Z` matches the shape and parses in
    // some engines by rolling over. Re-emitting must reproduce the input.
    if (new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z') !== s) return null;
    return ms;
}

function toHexLower(bytes: Uint8Array): string {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i] as number;
        out += (b < 16 ? '0' : '') + b.toString(16);
    }
    return out;
}

/** 128-lowercase-hex SHA-512 fingerprint of a 1984-byte hybrid signing key. */
export async function subkeyFingerprintOf(publicKey: Uint8Array): Promise<string> {
    if (publicKey.length !== HYBRID_PUBLIC_KEY_LEN) throw new Error('subkey-not-hybrid');
    return toHexLower(await computeHybridSigningKeyFingerprint(publicKey));
}

function decodeBase64Exact(b64: string, expectedLen: number): Uint8Array | null {
    if (typeof b64 !== 'string' || b64.length === 0) return null;
    let bytes: Uint8Array;
    try {
        bytes = aes.fromBase64(b64);
    } catch {
        return null;
    }
    return bytes.length === expectedLen ? bytes : null;
}

/** Canonical (RFC 8785 / JCS) signing bytes for a cert body. Exported so
 *  cross-implementation golden vectors can pin them. */
export function canonicalCertBytes(body: ChatSignCertBody): Uint8Array {
    return canonicalize(body as unknown as { [k: string]: string });
}

/** Canonical (RFC 8785 / JCS) signing bytes for a revocation body. */
export function canonicalRevocationBytes(body: ChatSignRevocationBody): Uint8Array {
    return canonicalize(body as unknown as { [k: string]: string });
}

function signCanonical(bytes: Uint8Array, rootSecretSeed: Uint8Array): string {
    return aes.toBase64(wrapperToCombinedBytes(signHybrid(bytes, rootSecretSeed)));
}

function certFail(
    code: CertVerifyFailureCode, message: string, ignoredRevocations = 0,
): CertVerifyResult {
    return { ok: false, code, message, ignoredRevocations };
}

// ── issuance ────────────────────────────────────────────────────────────

/**
 * Issue a cert binding `subkeyPubkey` to the account under the root held as
 * `rootSecretSeed`. Spec §6.
 *
 * `rootFingerprint` is derived from the seed we are about to SIGN with, never
 * from caller input: a caller-supplied fingerprint that disagreed with the
 * signing key would produce a cert failing verification rule 12 with no useful
 * diagnosis.
 *
 * The caller's seed buffer is neither mutated nor retained. Zeroizing it is
 * the CALLER's job (it owns the buffer's lifetime — the ceremony handler and
 * the headless driver both zero in a `finally`).
 *
 * Throws `Error` whose message is a stable code: `bad-root-seed`,
 * `bad-account-cid`, `subkey-not-hybrid`, `subkey-is-root`,
 * `validity-out-of-range`.
 */
export async function issueCert(
    rootSecretSeed: Uint8Array,
    subkeyPubkey: Uint8Array,
    opts: IssueCertOptions,
): Promise<ChatSignCert> {
    if (!(rootSecretSeed instanceof Uint8Array) || rootSecretSeed.length !== HYBRID_SECRET_SEED_LEN) {
        throw new Error('bad-root-seed');
    }
    if (typeof opts.accountCid !== 'string' || opts.accountCid.length === 0
        || opts.accountCid.length > MAX_ACCOUNT_CID_LEN) {
        throw new Error('bad-account-cid');
    }
    // §6 rule 1 / §17 trap — the issuer-side mirror of verification rule 7.
    if (!(subkeyPubkey instanceof Uint8Array) || subkeyPubkey.length !== HYBRID_PUBLIC_KEY_LEN) {
        throw new Error('subkey-not-hybrid');
    }

    const days = opts.validityDays ?? CHAT_CERT_DEFAULT_VALIDITY_DAYS;
    if (!Number.isInteger(days) || days < 1 || days > CHAT_CERT_MAX_VALIDITY_DAYS) {
        throw new Error('validity-out-of-range');
    }

    const rootPubkey = signingPublicKeyFromSeed(rootSecretSeed);

    // §6 rule 2: a root that certifies itself as a message-signing sub-key
    // collapses the two levels and puts the account root on the hot message
    // path — exactly what the two-level model exists to prevent.
    if (equalBytes(rootPubkey, subkeyPubkey)) throw new Error('subkey-is-root');

    const validFrom = opts.validFrom ?? new Date();
    const issuedAt = opts.issuedAt ?? new Date();
    const validFromIso = toCertTimestamp(validFrom);
    // Derive validTo from the SECOND-TRUNCATED start, so the emitted window is
    // exactly `days` long rather than `days` minus a sub-second remainder.
    const validToIso = toCertTimestamp(new Date(Date.parse(validFromIso) + days * MS_PER_DAY));

    const body: ChatSignCertBody = {
        type: CHAT_SIGN_CERT_TYPE,
        accountCid: opts.accountCid,
        rootFingerprint: toHexLower(await computeHybridSigningKeyFingerprint(rootPubkey)),
        subkeyPubkey: aes.toBase64(subkeyPubkey),
        validFrom: validFromIso,
        validTo: validToIso,
        issuedAt: toCertTimestamp(issuedAt),
    };

    return { body, signature: signCanonical(canonicalCertBytes(body), rootSecretSeed) };
}

/**
 * Issue a revocation for a sub-key named by fingerprint. Spec §4.
 *
 * Naming the sub-key by fingerprint rather than by public key is deliberate:
 * this is a statement about a possibly-compromised key and there is no reason
 * to carry 2 KB of it. One revocation kills the fingerprint across EVERY cert
 * ever issued for it, future re-certifications included.
 *
 * Throws `Error('bad-root-seed')` / `Error('bad-subkey-fingerprint')`.
 */
export async function issueRevocation(
    rootSecretSeed: Uint8Array,
    subkeyFingerprint: string,
    revokedAt?: Date,
): Promise<ChatSignCertRevocation> {
    if (!(rootSecretSeed instanceof Uint8Array) || rootSecretSeed.length !== HYBRID_SECRET_SEED_LEN) {
        throw new Error('bad-root-seed');
    }
    if (typeof subkeyFingerprint !== 'string' || !FINGERPRINT_RE.test(subkeyFingerprint)) {
        throw new Error('bad-subkey-fingerprint');
    }

    const rootPubkey = signingPublicKeyFromSeed(rootSecretSeed);
    const body: ChatSignRevocationBody = {
        type: CHAT_SIGN_REVOCATION_TYPE,
        rootFingerprint: toHexLower(await computeHybridSigningKeyFingerprint(rootPubkey)),
        subkeyFingerprint,
        revokedAt: toCertTimestamp(revokedAt ?? new Date()),
    };

    return { body, signature: signCanonical(canonicalRevocationBytes(body), rootSecretSeed) };
}

// ── verification ────────────────────────────────────────────────────────

/**
 * Verify a cert at instant `at`. Spec §5 — the rule numbers below are that
 * table's.
 *
 * Ordering: rule 12 (`root-mismatch`) runs BEFORE rule 13 (`bad-signature`) so
 * "you handed me the wrong root" stays distinguishable from "this is forged".
 * The rest is cheapest-first; the ML-DSA verify is by far the most expensive
 * step and runs last among the structural checks.
 *
 * Never throws on hostile input — every path returns a coded failure.
 */
export async function verifyCert(
    cert: unknown,
    rootPubkey: Uint8Array,
    opts: VerifyCertOptions,
): Promise<CertVerifyResult> {
    // Rules 1–3: envelope + closed field set.
    const parsed = readCertEnvelope(cert);
    if (!parsed) return certFail('malformed', 'not a chat_sign_cert.v1 envelope');
    const { body, signature } = parsed;

    // Rule 4.
    if (body.type !== CHAT_SIGN_CERT_TYPE) {
        return certFail('bad-type', `expected type ${CHAT_SIGN_CERT_TYPE}, got ${body.type}`);
    }
    // Rule 5.
    if (body.accountCid.length === 0 || body.accountCid.length > MAX_ACCOUNT_CID_LEN) {
        return certFail('malformed', 'accountCid must be 1..64 characters');
    }
    // Rule 6.
    if (!FINGERPRINT_RE.test(body.rootFingerprint)) {
        return certFail('malformed', 'rootFingerprint must be 128 lowercase hex');
    }
    // Rule 2.
    const signatureBytes = decodeBase64Exact(signature, HYBRID_COMBINED_SIGNATURE_LEN);
    if (!signatureBytes) {
        return certFail('bad-signature-encoding',
            `signature must be base64 of exactly ${HYBRID_COMBINED_SIGNATURE_LEN} bytes `
            + '(the combined hybrid form, never the wrapper JSON)');
    }
    // Rule 7 — the §17 trap, as a FORMAT refusal ahead of signature checking.
    const subkeyPubkey = decodeBase64Exact(body.subkeyPubkey, HYBRID_PUBLIC_KEY_LEN);
    if (!subkeyPubkey) {
        return certFail('subkey-not-hybrid',
            `subkeyPubkey must be base64 of exactly ${HYBRID_PUBLIC_KEY_LEN} bytes `
            + '(Ed25519+ML-DSA-65 hybrid); non-hybrid sub-keys are refused');
    }
    // Rule 8.
    const from = parseCertTimestamp(body.validFrom);
    const to = parseCertTimestamp(body.validTo);
    const issued = parseCertTimestamp(body.issuedAt);
    if (from === null || to === null || issued === null) {
        return certFail('bad-timestamp',
            'validFrom/validTo/issuedAt must be ISO-8601 UTC at second precision');
    }
    // Rule 9.
    if (to <= from) return certFail('bad-window', 'validTo must be strictly after validFrom');
    // Rule 10.
    if (to - from > CHAT_CERT_MAX_VALIDITY_DAYS * MS_PER_DAY) {
        return certFail('lifetime-too-long',
            `cert lifetime exceeds the ${CHAT_CERT_MAX_VALIDITY_DAYS}-day cap`);
    }
    // Rule 11.
    if (!(rootPubkey instanceof Uint8Array) || rootPubkey.length !== HYBRID_PUBLIC_KEY_LEN) {
        return certFail('bad-root-key',
            `root public key must be ${HYBRID_PUBLIC_KEY_LEN} bytes`);
    }
    // Rule 12 — before rule 13, deliberately.
    const rootFingerprint = toHexLower(await computeHybridSigningKeyFingerprint(rootPubkey));
    if (rootFingerprint !== body.rootFingerprint) {
        return certFail('root-mismatch',
            'cert names a different root than the key supplied for verification');
    }
    // Rule 13 — both halves; verifyHybrid computes each into its own variable.
    if (!verifyHybrid(canonicalCertBytes(body), combinedToWrapper(signatureBytes), rootPubkey)) {
        return certFail('bad-signature', 'hybrid signature does not verify under the root key');
    }

    // Rule 14 — inclusive at both ends.
    const at = opts.at instanceof Date ? opts.at.getTime() : NaN;
    if (Number.isNaN(at)) return certFail('bad-timestamp', 'evaluation instant `at` is not a Date');
    if (at < from) return certFail('not-yet-valid', `cert is not valid until ${body.validFrom}`);
    if (at > to) return certFail('expired', `cert expired at ${body.validTo}`);

    // Rule 15 — revocations, each authenticated before it is honoured.
    const subkeyFingerprint = toHexLower(await computeHybridSigningKeyFingerprint(subkeyPubkey));
    let ignoredRevocations = 0;
    for (const candidate of opts.revocations ?? []) {
        const shape = readRevocationEnvelope(candidate);
        // Not addressed to this root — not our business, and not an attack
        // signal either. Silently skipped, NOT counted as ignored.
        if (!shape || shape.body.rootFingerprint !== rootFingerprint) continue;

        const verified = await verifyRevocation(candidate, rootPubkey);
        if (!verified.ok) {
            // A forged revocation must NEVER kill a key: anyone who can inject
            // bytes into a control message would otherwise be able to disable
            // an arbitrary device (spec §4.3).
            ignoredRevocations++;
            continue;
        }
        if (verified.body.subkeyFingerprint !== subkeyFingerprint) continue;
        const revokedAt = parseCertTimestamp(verified.body.revokedAt);
        if (revokedAt === null) { ignoredRevocations++; continue; }
        // Effective from `revokedAt` ONWARD — not retroactive. Signatures made
        // earlier stay valid, which is what keeps sealed history verifiable.
        if (revokedAt <= at) {
            return certFail('revoked',
                `sub-key was revoked at ${verified.body.revokedAt}`, ignoredRevocations);
        }
    }

    return { ok: true, body, subkeyPubkey, subkeyFingerprint, ignoredRevocations };
}

/**
 * Verify a revocation's own signature under the root that supposedly issued it.
 * Callers MUST do this before honouring one; `verifyCert` does it internally
 * for everything on its `revocations` list.
 */
export async function verifyRevocation(
    revocation: unknown,
    rootPubkey: Uint8Array,
): Promise<RevocationVerifyResult> {
    const parsed = readRevocationEnvelope(revocation);
    if (!parsed) {
        return { ok: false, code: 'malformed', message: 'not a chat_sign_cert_revocation.v1 envelope' };
    }
    const { body, signature } = parsed;

    if (body.type !== CHAT_SIGN_REVOCATION_TYPE) {
        return { ok: false, code: 'bad-type', message: `expected type ${CHAT_SIGN_REVOCATION_TYPE}` };
    }
    if (!FINGERPRINT_RE.test(body.rootFingerprint) || !FINGERPRINT_RE.test(body.subkeyFingerprint)) {
        return { ok: false, code: 'malformed', message: 'fingerprints must be 128 lowercase hex' };
    }
    if (parseCertTimestamp(body.revokedAt) === null) {
        return { ok: false, code: 'bad-timestamp', message: 'revokedAt must be ISO-8601 UTC at second precision' };
    }
    const signatureBytes = decodeBase64Exact(signature, HYBRID_COMBINED_SIGNATURE_LEN);
    if (!signatureBytes) {
        return {
            ok: false, code: 'bad-signature-encoding',
            message: `signature must be base64 of exactly ${HYBRID_COMBINED_SIGNATURE_LEN} bytes`,
        };
    }
    if (!(rootPubkey instanceof Uint8Array) || rootPubkey.length !== HYBRID_PUBLIC_KEY_LEN) {
        return { ok: false, code: 'bad-root-key', message: 'root public key must be 1984 bytes' };
    }
    const rootFingerprint = toHexLower(await computeHybridSigningKeyFingerprint(rootPubkey));
    if (rootFingerprint !== body.rootFingerprint) {
        return {
            ok: false, code: 'root-mismatch',
            message: 'revocation names a different root than the key supplied',
        };
    }
    if (!verifyHybrid(canonicalRevocationBytes(body), combinedToWrapper(signatureBytes), rootPubkey)) {
        return { ok: false, code: 'bad-signature', message: 'hybrid signature does not verify' };
    }
    return { ok: true, body };
}

// ── envelope readers (closed field set, all-strings) ────────────────────

function readEnvelope(value: unknown): { body: Record<string, unknown>; signature: string } | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const v = value as { body?: unknown; signature?: unknown };
    if (!v.body || typeof v.body !== 'object' || Array.isArray(v.body)) return null;
    if (typeof v.signature !== 'string') return null;
    return { body: v.body as Record<string, unknown>, signature: v.signature };
}

/**
 * Closed field set: exactly the §3.1 keys, every value a string.
 *
 * A signature over canonicalized bytes already fails when a field is injected
 * after signing — JCS commits to every key. This additionally refuses a
 * SIGNER who invents fields, so producers and consumers cannot drift into a
 * de-facto v1.5.
 */
function readCertEnvelope(value: unknown): { body: ChatSignCertBody; signature: string } | null {
    const env = readEnvelope(value);
    if (!env) return null;
    if (!hasExactStringFields(env.body, CERT_BODY_FIELDS)) return null;
    return { body: env.body as unknown as ChatSignCertBody, signature: env.signature };
}

function readRevocationEnvelope(
    value: unknown,
): { body: ChatSignRevocationBody; signature: string } | null {
    const env = readEnvelope(value);
    if (!env) return null;
    if (!hasExactStringFields(env.body, REVOCATION_BODY_FIELDS)) return null;
    return { body: env.body as unknown as ChatSignRevocationBody, signature: env.signature };
}

function hasExactStringFields(
    body: Record<string, unknown>, fields: readonly string[],
): boolean {
    const keys = Object.keys(body);
    if (keys.length !== fields.length) return false;
    for (const field of fields) {
        if (typeof body[field] !== 'string') return false;
    }
    return true;
}

function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}

// ── serialization ───────────────────────────────────────────────────────
//
// Provided so callers never hand-roll the envelope. The output is ordinary
// JSON, NOT canonical JSON — the canonical form is the SIGNED form of `body`
// only, and a verifier re-derives it. Keeping the two distinct is what makes
// transit re-ordering harmless.

export function certToJson(cert: ChatSignCert): string {
    return JSON.stringify(cert);
}

export function parseCert(json: string): ChatSignCert | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch {
        return null;
    }
    const env = readCertEnvelope(parsed);
    return env ? { body: env.body, signature: env.signature } : null;
}

export function revocationToJson(revocation: ChatSignCertRevocation): string {
    return JSON.stringify(revocation);
}

export function parseRevocation(json: string): ChatSignCertRevocation | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch {
        return null;
    }
    const env = readRevocationEnvelope(parsed);
    return env ? { body: env.body, signature: env.signature } : null;
}
